import { apiOrigin, isLocalHost } from '@/lib/apiOrigin'

const hostname = window && window.location && window.location.hostname

// Backend origin for every browser-side call. `apiOrigin()` already answers both cases: the
// dev backend's own port locally, and the current origin on any real hostname, where Caddy
// serves frontend and backend together (see lib/apiOrigin.js, which documents exactly that).
//
// This used to be assigned per-host inside the Turnstile chain below, which had no `else`.
// Any hostname not named there got `API_ROOT === undefined`, so every request went to
// '/undefined/api/...' — a path the SPA catch-all answers with index.html, i.e. HTTP 200 that
// is not JSON. On joulelingo.com and kilosight.com that made BrandingContext's shape guard
// throw and fall back to the default 'Power' brand, so both brand domains rendered with the
// neutral name, no brand colours and the wrong wordmark, while every other API call failed
// the same silent way. Deriving it from the origin also means a new brand domain needs no
// change here — which is what let this slip in the first place.
const API_ROOT = apiOrigin()

// Turnstile site keys, unlike the API origin, genuinely cannot be derived: Cloudflare scopes
// each key to a fixed hostname list, so this stays an explicit map. A host that is missing
// here returns `undefined`, which renders a widget that can never verify — signup, password
// recovery and the contact form are then unsubmittable on that host, so a new domain must be
// added to a Turnstile widget and listed below.
export const TURNSTILE_KEY = (() => {
    if (isLocalHost(hostname)) return '3x00000000000000000000FF'
    if (hostname === 'testapps.fettes.com') return '0x4AAAAAAAaeqcternfKLupV'
    if (hostname === 'apps.fettes.com') return '0x4AAAAAAAaerQt5MhOZAVXY'
    // Shared rosslyoung.com Turnstile key — same widget as
    // caskmanager.rosslyoung.com since both are subdomains of it.
    if (hostname === 'power.rosslyoung.com') return '0x4AAAAAAESs-p6PHxkd9rC-'
    // The two brand domains share one widget, which is scoped to exactly these two hostnames —
    // not to power.rosslyoung.com above, even though the same hetz deployment serves all three.
    // That is why the backend resolves the siteverify secret per request host
    // (settings.TURNSTILE_SECRET_KEYS): a token from this widget only verifies against this
    // widget's secret. No www. variants, matching ALLOWED_HOSTS — neither resolves.
    if (hostname === 'joulelingo.com' || hostname === 'kilosight.com') {
        return '0x4AAAAAAFgH5UqKj2gmUfmh'
    }
    return undefined
})()

// export const ACCOUNT_REGISTER  = `${API_ROOT}/api/auth/register/`
// export const ACCOUNT_LOGIN     = `${API_ROOT}/api/auth/login/`
// export const ACCOUNT_LOGOUT    = `${API_ROOT}/api/auth/logout/`
// export const ACCOUNT_DETAILS   = `${API_ROOT}/api/account/me/`
// export const ACCOUNT_SET_CSRF  = `${API_ROOT}/api/account/set-csrf/`
// export const ACCOUNT_VERIFY_EMAIL_KEY     = `${API_ROOT}/api/auth/account-confirm-email/`
// export const ACCOUNT_VERIFY_EMAIL_RESEND  = `${API_ROOT}/api/auth/register/resend-email/`
// export const ACCOUNT_PASSWORD_RESET          = `${API_ROOT}/api/auth/password/reset/`
// export const ACCOUNT_PASSWORD_RESET_CONFIRM  = `${API_ROOT}/api/auth/password/reset/confirm/`
// export const ACCOUNT_PASSWORD_CHANGE         = `${API_ROOT}/api/auth/password/change/`

// we can possibly use https://docs.allauth.org/_allauth/{client}/v1/auth/session and add the csrftoken to the response?
export const ACCOUNT_REGISTER = `${API_ROOT}/api/auth/browser/v1/auth/signup`
export const ACCOUNT_LOGIN = `${API_ROOT}/api/auth/browser/v1/auth/login`
export const ACCOUNT_LOGOUT = `${API_ROOT}/api/auth/browser/v1/auth/session`
export const ACCOUNT_DETAILS = `${API_ROOT}/api/account/me/`
export const ACCOUNT_SET_CSRF = `${API_ROOT}/api/account/set-csrf/` // can we remove this?
export const ACCOUNT_VERIFY_EMAIL_KEY = `${API_ROOT}/api/auth/browser/v1/auth/email/verify`
export const ACCOUNT_VERIFY_EMAIL_RESEND = `${API_ROOT}/api/auth/browser/v1/auth/email/verify/resend`
export const ACCOUNT_PASSWORD_RESET = `${API_ROOT}/api/auth/browser/v1/auth/password/request`
export const ACCOUNT_PASSWORD_RESET_CONFIRM = `${API_ROOT}/api/auth/browser/v1/auth/password/reset`
export const ACCOUNT_PASSWORD_CHANGE = `${API_ROOT}/api/auth/browser/v1/account/password/change`

export const CONTACT_FORM = `${API_ROOT}/api/core/contact/`
export const PROJECT_COSTS = `${API_ROOT}/api/core/project-costs/`

export const GEOCODE_URL = 'https://geocode.rosslyoung.com/api/'

// this is where we post to allauth to get the redirect to MS login
export const ACCOUNT_MS_GRAPH_LOGIN = `${API_ROOT}/api/auth/microsoft/login/`

export const LOCATIONS = `${API_ROOT}/api/location/`
export const LOCATION_BULK = `${API_ROOT}/api/location/bulk/`
export const LOCATION_TYPES = `${API_ROOT}/api/location/type/`
export const LOCATION_PARENT = `${API_ROOT}/api/location/update_location_parent/`
export const LOCATION_COLOR = `${API_ROOT}/api/location/update_location_color/`

export const LOCATION_SUGGESTIONS = `${API_ROOT}/api/location/suggestions/`
export const LOCATION_SUGGESTION_ACCEPT = `${API_ROOT}/api/location/accept_suggestion/`
export const LOCATION_SUGGESTION_REJECT = `${API_ROOT}/api/location/reject_suggestion/`

export const MARKETING = `${API_ROOT}/api/marketing/`
export const MARKETING_CHOICES = `${API_ROOT}/api/marketing/choices/`
export const MARKETING_ORGANIZATIONS = `${API_ROOT}/api/marketing/organizations/`
export const MARKETING_ORGANIZATION_DETAIL = (id) =>
    `${API_ROOT}/api/marketing/organizations/${id}/`
export const MARKETING_CONTACTS = `${API_ROOT}/api/marketing/contacts/`
export const MARKETING_CONTACT_DETAIL = (id) =>
    `${API_ROOT}/api/marketing/contacts/${id}/`
export const MARKETING_LOCATIONS = `${API_ROOT}/api/marketing/locations/`
export const MARKETING_LOCATION_DETAIL = (id) =>
    `${API_ROOT}/api/marketing/locations/${id}/`
export const MARKETING_ACTIVITIES = `${API_ROOT}/api/marketing/activities/`
export const MARKETING_ACTIVITY_DETAIL = (id) =>
    `${API_ROOT}/api/marketing/activities/${id}/`
export const MARKETING_TEMPLATES = `${API_ROOT}/api/marketing/templates/`
export const MARKETING_TEMPLATE_DETAIL = (id) =>
    `${API_ROOT}/api/marketing/templates/${id}/`

export const NOTIFICATIONS = `${API_ROOT}/api/notification/`
export const NOTIFICATION_WS_TOKEN = `${API_ROOT}/api/notification/ws-token/`
export const NOTIFICATION_ARCHIVE = `${API_ROOT}/api/notification/archive/`
export const NOTIFICATION_ARCHIVE_ALL = `${API_ROOT}/api/notification/archive-all/`
export const ORG = `${API_ROOT}/api/org/`
export const ORG_ACTIVE = `${ORG}active/`
export const ORG_CURRENT = `${ORG}current/`
// org-sidebar-logo D5: PUT multipart {file, kind} to upload/replace, DELETE
// with ?kind= to remove. kind is 'logo' (default) or 'mark'.
export const ORG_CURRENT_LOGO = `${ORG_CURRENT}logo/`
// self-serve-org-signup: authenticated org creation for a verified, org-less,
// partner-less user. Body {name, flavour}; no domain field — the claimed domain is
// derived server-side from the user's verified email (design decision 2).
export const ORG_SELF_SERVE = `${ORG}self-serve/`
// Which partner can open the active org, its staff and their recent activity, for the
// org's own admins (org/partner_access.py).
export const ORG_CURRENT_PARTNER_ACCESS = `${ORG_CURRENT}partner-access/`
// Site admins: GET ?partner=<id> previews moving an org to another partner (blank: billed
// directly by us); POST {partner, reason} moves it (org/partner_moves.py).
export const ORG_MOVE_PARTNER = (orgId) => `${ORG}${orgId}/move-partner/`
// Organisation terms and DPA acceptance (agreements app): GET the published version and the
// active org's acceptance; POST {version, role, confirm} to accept for your own org.
export const AGREEMENTS_TERMS = `${API_ROOT}/api/agreements/terms/`
export const AGREEMENTS_TERMS_ACCEPT = `${AGREEMENTS_TERMS}accept/`
// self-serve-org-signup task 6.2: what deleting an org would destroy — every user
// account (User.org cascades on delete), meters, locations, and whether it has reading
// history. Read before DELETE `${ORG}<id>/`, which also takes a {confirm_name} body.
export const ORG_DELETION_IMPACT = (orgId) => `${ORG}${orgId}/deletion-impact/`
// Read-only org summary for those who oversee an org from outside it: site admins, the
// partner admins whose partner manages it, and admins of a group it belongs to.
export const ORG_SUMMARY = (orgId) => `${ORG}${orgId}/summary/`
export const PARTNER_ORGS = `${ORG}partner/orgs/`
export const PARTNER_ORG_BULK = `${ORG}partner/orgs/bulk/`
export const ORG_BULK = `${ORG}bulk/`
export const PARTNER_LICENSE = `${ORG}partner/license/`
export const PARTNER_PORTFOLIO = `${ORG}partner/portfolio/`
// The active org's per-property portfolio, behind the Properties / Sites league page
// (openspec: property-league-and-house-standings): readable by the org's staff roles
// (PROPERTY_LEAGUE_ROLES) when `houses` is on, with cost fields null unless the viewer
// may see cost. Rows carry a meter-category breakdown once any electricity meter is
// categorised (openspec: meter-categories).
export const PROPERTY_PORTFOLIO = `${ORG}current/property-portfolio/`
export const ADMIN_PARTNERS = `${ORG}admin/partners/`
export const ORG_GROUPS = `${ORG}groups/`
export const GROUP_DASHBOARD = (groupId) => `${ORG}groups/${groupId}/dashboard/`
export const ADMIN_ORG_GROUPS = `${ORG}admin/groups/`
export const ADMIN_ORG_GROUP = (groupId) => `${ORG}admin/groups/${groupId}/`
export const ADMIN_ORG_GROUP_MEMBERS = (groupId) =>
    `${ORG}admin/groups/${groupId}/members/`
export const ADMIN_ORG_GROUP_ADMINS = (groupId) =>
    `${ORG}admin/groups/${groupId}/admins/`
export const ORG_CURRENT_EXTERNAL_SERVICES = `${ORG}current/external-services/`
export const ORG_CURRENT_EXTERNAL_SERVICES_AUTH = `${ORG}current/external-services/auth/`
export const ORG_CURRENT_EXTERNAL_SERVICES_ELECTRICITY = `${ORG}current/external-services/electricity/`
export const ORG_CURRENT_EXTERNAL_SERVICES_GAS = `${ORG}current/external-services/gas/`
export const ORG_CURRENT_EXTERNAL_SERVICES_WATER = `${ORG}current/external-services/water/`
export const ORG_CURRENT_EXTERNAL_SERVICES_MIS = `${ORG}current/external-services/mis/`
export const ORG_CURRENT_EXTERNAL_SERVICES_AUTH_TEST = `${ORG}current/external-services/auth/test/`
export const ORG_CURRENT_EXTERNAL_SERVICES_ELECTRICITY_TEST = `${ORG}current/external-services/electricity/test/`
export const ORG_CURRENT_EXTERNAL_SERVICES_GAS_TEST = `${ORG}current/external-services/gas/test/`
export const ORG_CURRENT_EXTERNAL_SERVICES_WATER_TEST = `${ORG}current/external-services/water/test/`
export const ORG_CURRENT_EXTERNAL_SERVICES_MIS_TEST = `${ORG}current/external-services/mis/test/`
export const ORG_CURRENT_EXTERNAL_SERVICES_UTILITY_FETCH = (utilityType) =>
    `${ORG}current/external-services/${utilityType}/fetch/`
// Every connection the org holds for a utility, across all scopes — the candidate list for
// pinning a single meter to a specific connection.
export const ORG_CURRENT_PROVIDER_CONFIGS = (utilityType) =>
    `${ORG}current/provider-configs/${utilityType}/`
export const ORG_CURRENT_SETUP_STATUS = `${ORG}current/setup-status/`
export const ORG_CURRENT_SETUP_COMPLETE = `${ORG}current/setup-complete/`
// Term calendar (openspec term-calendar-import): status + academic year start, the
// curated published calendars, and the preview → commit → undo holiday importer.
export const ORG_TERM_CALENDAR = (orgId) => `${ORG}${orgId}/term-calendar/`
export const TERM_CALENDARS_PUBLISHED = `${ORG}term-calendars/published/`
export const ORG_HOLIDAY_IMPORT = (orgId) => `${ORG}${orgId}/holidays/import/`
export const ORG_HOLIDAY_IMPORT_PREVIEW = (orgId) =>
    `${ORG}${orgId}/holidays/import/preview/`
export const ORG_HOLIDAY_IMPORT_COMMIT = (orgId) =>
    `${ORG}${orgId}/holidays/import/commit/`
export const ORG_HOLIDAY_IMPORT_UNDO = (orgId, batchId) =>
    `${ORG}${orgId}/holidays/import/${batchId}/undo/`
export const SEARCH = `${API_ROOT}/api/search/`

// org-onboarding-hub: endpoint lands in a later wave (task 3.3); declared ahead of time
// so useOnboardingPlan can be written against its documented contract now.
export const ONBOARDING = `${API_ROOT}/api/onboarding/`
export const ONBOARDING_CURRENT_PLAN = `${ONBOARDING}current/plan/`
export const ONBOARDING_CURRENT_STEP = (stepId) =>
    `${ONBOARDING}current/steps/${stepId}/`
export const ONBOARDING_CURRENT_CAPABILITIES = `${ONBOARDING}current/capabilities/`
export const ONBOARDING_PARTNER_SUMMARY = `${ONBOARDING}partner/summary/`
// The "What we need from you" form (onboarding/intake.py): GET the questions for the active
// org's type and its saved answers, PUT {answers} to save a draft, POST {answers} to submit.
export const ONBOARDING_CURRENT_INTAKE = `${ONBOARDING}current/intake/`
export const ONBOARDING_CURRENT_INTAKE_SUBMIT = `${ONBOARDING_CURRENT_INTAKE}submit/`

export const AUTH_TENANT_DISCOVER = `${API_ROOT}/api/auth/tenant/discover/`
export const AUTH_TENANT_REDIRECT = `${API_ROOT}/api/auth/tenant/redirect/`

export const POWER_ALERTS = `${API_ROOT}/api/power/alerts/`

export const ASSISTANT_MESSAGE = `${API_ROOT}/api/assistant/message/`
export const ASSISTANT_CONVERSATIONS = `${API_ROOT}/api/assistant/conversations/`

export const ALERTS = `${API_ROOT}/api/alerts/`
export const ALERT_SPIKE_NOW = `${API_ROOT}/api/alerts/spike-now/`
export const ALERT_RULES = `${API_ROOT}/api/alerts/rules/`
export const ALERT_RULE_TEMPLATES = `${API_ROOT}/api/alerts/rules/templates/`
export const ALERT_NOTIFICATION_PREFERENCE = `${API_ROOT}/api/alerts/notification-preference/`

// Staff-facing org users for the alert-rule recipient picker (admin-gated).
export const ALERT_RECIPIENT_USERS = `${API_ROOT}/api/alerts/recipient-users/`

// Staff-facing org users for the alert-detail assignee picker (staff-gated).
export const ALERT_ASSIGNABLE_USERS = `${API_ROOT}/api/alerts/assignable-users/`

// Staff-gated alert workflow actions (note: no trailing slash, per the backend routes).
export const ALERT_ACKNOWLEDGE = (id) =>
    `${API_ROOT}/api/alerts/${id}/acknowledge`
export const ALERT_RESOLVE = (id) => `${API_ROOT}/api/alerts/${id}/resolve`
export const ALERT_ASSIGN = (id) => `${API_ROOT}/api/alerts/${id}/assign`
export const ALERT_REOPEN = (id) => `${API_ROOT}/api/alerts/${id}/reopen`

// Meter options for the alert-rule target picker (reuse the per-utility meter list endpoints).
export const ALERT_TARGET_METERS_POWER = `${API_ROOT}/api/power/meter/`
export const ALERT_TARGET_METERS_GAS = `${API_ROOT}/api/gas/meter/`

// Analytics (read-only derived insights). GET .../{start}/{end}/
export const ANALYTICS_BASELOAD = `${API_ROOT}/api/analytics/baseload/`
export const ANALYTICS_LOAD_SHAPE = `${API_ROOT}/api/analytics/load-shape/`
export const ANALYTICS_CLUSTERS = `${API_ROOT}/api/analytics/clusters/`
export const ANALYTICS_HEATMAP = `${API_ROOT}/api/analytics/heatmap/`
export const ANALYTICS_WEATHER_NORMALISED = `${API_ROOT}/api/analytics/weather-normalised/`
export const ANALYTICS_CARBON = `${API_ROOT}/api/analytics/carbon/`
export const ANALYTICS_BENCHMARK = `${API_ROOT}/api/analytics/benchmark/`
export const ANALYTICS_HOLIDAYS = `${API_ROOT}/api/analytics/holidays/`

export const POWER_EXPLORE = `${API_ROOT}/api/power/explore/`
export const POWER_HEATMAP = `${API_ROOT}/api/power/heatmap/`
export const POWER_RAW_VALUES = `${API_ROOT}/api/power/raw-values/`
export const POWER_STATISTICS = `${API_ROOT}/api/power/statistics/`
export const GAS_METERS = `${API_ROOT}/api/gas/meter/`
export const GAS_EXPLORE = `${API_ROOT}/api/gas/explore/`
export const POWER_METERS = `${API_ROOT}/api/power/meter/`
// Member-readable (every member role): the Overview energy-mix card and Class Projects' meter list.
export const POWER_ENERGY_MIX = `${API_ROOT}/api/power/energy-mix/`
export const POWER_METER_OPTIONS = `${API_ROOT}/api/power/meter-options/`
export const WATER_METERS = `${API_ROOT}/api/water/meter/`
export const POWER_OVERVIEW = `${API_ROOT}/api/power/overview/`
export const POWER_TEAM_GOAL = `${API_ROOT}/api/power/team-goal/`
export const POWER_JOLT_TIP = `${API_ROOT}/api/power/jolt-tip/`
export const POWER_METER_VISUALIZATION_FRAMES = `${API_ROOT}/api/power/meter-visualization/frames`
export const POWER_METER_VISUALIZATION_META = `${API_ROOT}/api/power/meter-visualization/meta`
export const POWER_READING_REFRESH = `${API_ROOT}/api/power/refresh-meter-data/`
export const POWER_READING_REFRESH_STATUS = `${API_ROOT}/api/power/refresh-meter-data/status/`

// Energy quiz
export const QUIZ_QUESTIONS = `${API_ROOT}/api/quiz/questions/`
export const QUIZ_LEADERBOARD = `${API_ROOT}/api/quiz/leaderboard/`
export const quizSubmitUrl = (sessionId) =>
    `${API_ROOT}/api/quiz/sessions/${sessionId}/submit/`
// Records one question's answer and returns that question's verdict. The only
// route by which a correct answer reaches the client before the round ends —
// and only for a question already committed to. See QuizAnswerView.
export const quizAnswerUrl = (sessionId, questionId) =>
    `${API_ROOT}/api/quiz/sessions/${sessionId}/questions/${questionId}/answer/`
// Org quiz settings (name display, default level). PATCH needs can_edit.
export const QUIZ_SETTINGS = `${API_ROOT}/api/quiz/settings/`
// This week's challenge status for the viewer (not_started/in_progress/completed).
export const QUIZ_CHALLENGE = `${API_ROOT}/api/quiz/challenge/`
export const QUIZ_BADGES = `${API_ROOT}/api/quiz/badges/`
// Question of the day; `?reveal=1` adds the answer for teacher roles.
export const QUIZ_DAILY = `${API_ROOT}/api/quiz/daily/`
export const QUIZ_DAILY_ANSWER = `${API_ROOT}/api/quiz/daily/answer/`
// Teacher-led class round, answers included. Nothing is persisted.
export const QUIZ_CLASS = `${API_ROOT}/api/quiz/class/`
// Teacher insights for one challenge week (`?week=YYYY-MM-DD`).
export const QUIZ_INSIGHTS = `${API_ROOT}/api/quiz/insights/`

export const POWER_METERS_READINGS = `${API_ROOT}/api/power/readings/`
export const POWER_EQUIVALENTS = `${API_ROOT}/api/power/equivalents/`
export const POWER_EXPORT = `${API_ROOT}/api/power/export/`

// Org-wide weather (temperature overlay for energy charts). GET ?start=&end=
export const WEATHER = `${API_ROOT}/api/weather/`
// Site-admin cross-org weather review (read-only). GET ?org=&start=&end=
export const WEATHER_ADMIN = `${API_ROOT}/api/weather/admin/`

// Staff page feedback: POST {path, page_title, rating, comment}. Site admins read
// FEEDBACK_INBOX (?status=open|resolved), `summary/`, `by-page/`, and POST `${id}/resolve/`
// {resolved} or `${id}/reply/` {reply}. FEEDBACK_MINE is the caller's own, with replies.
export const FEEDBACK = `${API_ROOT}/api/feedback/`
export const FEEDBACK_INBOX = `${API_ROOT}/api/feedback/inbox/`
export const FEEDBACK_MINE = `${API_ROOT}/api/feedback/mine/`

// Platform feature flags (backend core/feature_flags.py): switches for every organisation, not
// organisation features (Org.feature_flags). FEATURE_FLAGS is public: {key: bool} for the user-facing
// ones. SITE_ADMIN_FEATURE_FLAGS: GET {categories, flags}; `${key}/` GET (with history),
// PUT {enabled, reason}, DELETE {reason}.
export const FEATURE_FLAGS = `${API_ROOT}/api/core/features/`
export const SITE_ADMIN_FEATURE_FLAGS = `${API_ROOT}/api/site-admin/feature-flags/`

export const SITE_ADMIN = `${API_ROOT}/api/site-admin/`
export const SITE_ADMIN_REPORTS = `${API_ROOT}/api/site-admin/reports/`
export const SITE_ADMIN_REPORTS_BULK_DISMISS = `${API_ROOT}/api/site-admin/reports/bulk-dismiss/`
export const SITE_ADMIN_USERS = `${API_ROOT}/api/site-admin/users/`
export const SITE_ADMIN_STATS = `${API_ROOT}/api/site-admin/stats/`
export const SITE_ADMIN_STATS_TRENDS = `${API_ROOT}/api/site-admin/stats/trends/`
export const SITE_ADMIN_AUDIT_LOG = `${API_ROOT}/api/site-admin/audit-log/`
export const SITE_ADMIN_UTILITY_FETCH_STATUS = `${API_ROOT}/api/site-admin/utility-fetch-status/`
export const UTILITY_FETCH_STATUS = `${API_ROOT}/api/utility-fetch/fetch-status/`
// Org-scoped meter data-quality ledger (read-only). See utility_fetchers/DATA_QUALITY.md.
export const UTILITY_QUALITY_FINDINGS = `${API_ROOT}/api/utility-fetch/quality/findings/`
export const UTILITY_QUALITY_SUMMARY = `${API_ROOT}/api/utility-fetch/quality/summary/`
// Org-scoped reconciliation gap ledger (read-only). See utility_fetchers/RECONCILIATION.md.
export const UTILITY_READING_GAPS = `${API_ROOT}/api/utility-fetch/gaps/`
// Manual CSV/XLSX reading import (org admin + site admin). See csv-meter-ingestion.
export const UTILITY_READING_IMPORTS = `${API_ROOT}/api/utility-fetch/reading-imports/`
export const UTILITY_READING_IMPORT_PREVIEW = `${API_ROOT}/api/utility-fetch/reading-imports/preview/`
export const UTILITY_READING_IMPORT_TEMPLATES = `${API_ROOT}/api/utility-fetch/reading-imports/templates/`
// Site-admin cross-org meter data-quality ledger + action levers. See utility_fetchers/DATA_QUALITY.md.
export const SITE_ADMIN_UTILITY_QUALITY = `${API_ROOT}/api/site-admin/utility-quality/`
export const SITE_ADMIN_UTILITY_QUALITY_BY_PROVIDER = `${API_ROOT}/api/site-admin/utility-quality/by-provider/`
export const SITE_ADMIN_UTILITY_QUALITY_RUN_SCAN = `${API_ROOT}/api/site-admin/utility-quality/run-scan/`
export const SITE_ADMIN_UTILITY_QUALITY_RECOMPUTE_DAILY = `${API_ROOT}/api/site-admin/utility-quality/recompute-daily/`
export const SITE_ADMIN_UTILITY_QUALITY_REOPEN_GAP = `${API_ROOT}/api/site-admin/utility-quality/reopen-gap/`
export const SITE_ADMIN_UTILITY_READING_GAPS = `${API_ROOT}/api/site-admin/utility-reading-gaps/`
export const SITE_ADMIN_UTILITY_QUALITY_MUTE = (id) =>
    `${API_ROOT}/api/site-admin/utility-quality/${id}/mute/`
export const SITE_ADMIN_UTILITY_QUALITY_UNMUTE = (id) =>
    `${API_ROOT}/api/site-admin/utility-quality/${id}/unmute/`
export const PUBLIC_METERONLINE_STATUS = `${API_ROOT}/api/utility-fetch/public/meteronline/`
export const PUBLIC_METERONLINE_STATUS_DETAIL = `${API_ROOT}/api/utility-fetch/public/meteronline-detail/`
export const SITE_ADMIN_RUN_CREATE_FACTORY_DATA = `${API_ROOT}/api/site-admin/tasks/run-create-factory-data/`
// Seeds one fully usable demo org per org type, each with a login (development only —
// the command refuses to run under a production configuration).
export const SITE_ADMIN_RUN_SEED_DEMO_ORGS = `${API_ROOT}/api/site-admin/tasks/run-seed-demo-orgs/`
export const SITE_ADMIN_RUN_LOCATION_TASKS = `${API_ROOT}/api/site-admin/tasks/run-location-tasks/`
export const SITE_ADMIN_RUN_MOCK_METER_READINGS = `${API_ROOT}/api/site-admin/tasks/run-mock-meter-readings/`
export const SITE_ADMIN_RUN_USER_TASKS = `${API_ROOT}/api/site-admin/tasks/run-user-tasks/`
export const SITE_ADMIN_RUN_FETCH_ELECTRICITY_READINGS = `${API_ROOT}/api/site-admin/tasks/run-fetch-electricity-readings/`
export const SITE_ADMIN_RUN_FETCH_GAS_READINGS = `${API_ROOT}/api/site-admin/tasks/run-fetch-gas-readings/`
export const SITE_ADMIN_RUN_WEATHER_FETCH = `${API_ROOT}/api/site-admin/tasks/run-weather-fetch/`
export const SITE_ADMIN_EXPORT_SAMPLE_DATA = `${API_ROOT}/api/site-admin/tasks/export-sample-data/`
export const SITE_ADMIN_IMPORT_SAMPLE_DATA = `${API_ROOT}/api/site-admin/tasks/import-sample-data/`
export const SITE_ADMIN_USER_DETAIL = (userId) =>
    `${API_ROOT}/api/site-admin/users/${userId}/`
export const SITE_ADMIN_USER_SUSPEND = (userId) =>
    `${API_ROOT}/api/site-admin/users/${userId}/suspend/`
export const SITE_ADMIN_USER_LIFT_SUSPENSION = (userId) =>
    `${API_ROOT}/api/site-admin/users/${userId}/lift-suspension/`
export const SITE_ADMIN_USER_WARN = (userId) =>
    `${API_ROOT}/api/site-admin/users/${userId}/warn/`
export const SITE_ADMIN_USER_PROMOTE = (userId) =>
    `${API_ROOT}/api/site-admin/users/${userId}/promote/`
// Reset someone's two-factor authentication: POST {reason}. Site admins, anyone but themselves.
export const SITE_ADMIN_USER_RESET_MFA = (userId) =>
    `${API_ROOT}/api/site-admin/users/${userId}/reset-mfa/`
export const SITE_ADMIN_ANNOUNCEMENTS = `${API_ROOT}/api/site-admin/announcements/`
export const SITE_ADMIN_ANNOUNCEMENT_SEND = (id) =>
    `${API_ROOT}/api/site-admin/announcements/${id}/send/`

// Site-admin fleet-wide billing view (all orgs, incl. those with no BillingAccount).
export const SITE_ADMIN_BILLING_ORGS = `${SITE_ADMIN}billing/orgs/`
export const SITE_ADMIN_BILLING_ORG_DETAIL = (orgId) =>
    `${SITE_ADMIN}billing/orgs/${orgId}/`
export const SITE_ADMIN_BILLING_ORG_OVERRIDE = (orgId) =>
    `${SITE_ADMIN}billing/orgs/${orgId}/override/`
export const SITE_ADMIN_BILLING_ORG_SYNC = (orgId) =>
    `${SITE_ADMIN}billing/orgs/${orgId}/sync/`
// Meter data access (org admin, partner admin, site admin; active org). An org asks Power
// to collect a meter's data (n3rgy DCC consent, Perse / data-feed Letter of Authority).
export const METER_DATA_AUTHORISATIONS = `${API_ROOT}/api/meter-data/authorisations/`
export const METER_DATA_AUTHORISATION_DECLARATION = `${API_ROOT}/api/meter-data/authorisations/declaration/`
export const METER_DATA_AUTHORISATION_METERS = `${API_ROOT}/api/meter-data/authorisations/meters/`
// A printable, pre-filled Letter of Authority (HTML download); perse and data_feed only.
export const METER_DATA_AUTHORISATION_LETTER = `${API_ROOT}/api/meter-data/authorisations/letter/`
export const METER_DATA_AUTHORISATION_EVIDENCE = (id) =>
    `${API_ROOT}/api/meter-data/authorisations/${id}/evidence/`
export const METER_DATA_AUTHORISATION_WITHDRAW = (id) =>
    `${API_ROOT}/api/meter-data/authorisations/${id}/withdraw/`
// Past data (history) requests: an org asks Power to get up to 12 months of past
// half-hourly data from its supplier, with a signed Letter of Authority.
export const METER_DATA_HISTORY_REQUESTS = `${API_ROOT}/api/meter-data/history-requests/`
export const METER_DATA_HISTORY_CHOICES = `${API_ROOT}/api/meter-data/history-requests/choices/`
export const METER_DATA_HISTORY_DECLARATION = `${API_ROOT}/api/meter-data/history-requests/declaration/`
// A printable, pre-filled Letter of Authority to the supplier (HTML download).
export const METER_DATA_HISTORY_LETTER = `${API_ROOT}/api/meter-data/history-requests/letter/`
export const METER_DATA_HISTORY_CANCEL = (id) =>
    `${API_ROOT}/api/meter-data/history-requests/${id}/cancel/`

// Site-admin supplier / data collector file feeds: sources, every file received, the
// feeds mailbox log, and the queue of meter access requests to review.
export const SITE_ADMIN_METER_FEED_SOURCES = `${SITE_ADMIN}meter-feeds/sources/`
export const SITE_ADMIN_METER_FEED_SOURCE = (id) =>
    `${SITE_ADMIN}meter-feeds/sources/${id}/`
export const SITE_ADMIN_METER_FEED_SOURCE_PREVIEW_FORMAT = `${SITE_ADMIN}meter-feeds/sources/preview-format/`
export const SITE_ADMIN_METER_FEED_SOURCE_UPLOAD = (id) =>
    `${SITE_ADMIN}meter-feeds/sources/${id}/upload/`
export const SITE_ADMIN_METER_FEED_SOURCE_CHECK_NOW = (id) =>
    `${SITE_ADMIN}meter-feeds/sources/${id}/check-now/`
export const SITE_ADMIN_METER_FEED_SOURCE_FORGET_HOST_KEY = (id) =>
    `${SITE_ADMIN}meter-feeds/sources/${id}/forget-host-key/`
export const SITE_ADMIN_METER_FEED_SOURCE_METERS = (id) =>
    `${SITE_ADMIN}meter-feeds/sources/${id}/meters/`
export const SITE_ADMIN_METER_FEED_FILES = `${SITE_ADMIN}meter-feeds/files/`
export const SITE_ADMIN_METER_FEED_FILE = (id) =>
    `${SITE_ADMIN}meter-feeds/files/${id}/`
export const SITE_ADMIN_METER_FEED_FILE_DOWNLOAD = (id) =>
    `${SITE_ADMIN}meter-feeds/files/${id}/download/`
export const SITE_ADMIN_METER_FEED_FILE_REPROCESS = (id) =>
    `${SITE_ADMIN}meter-feeds/files/${id}/reprocess/`
// Meter ID lookups: a sender's own meter IDs mapped onto MPANs/MPRNs, per source.
export const SITE_ADMIN_METER_FEED_ALIASES = `${SITE_ADMIN}meter-feeds/aliases/`
export const SITE_ADMIN_METER_FEED_ALIAS = (id) =>
    `${SITE_ADMIN}meter-feeds/aliases/${id}/`
export const SITE_ADMIN_METER_FEED_ALIASES_BULK = `${SITE_ADMIN}meter-feeds/aliases/bulk/`
export const SITE_ADMIN_METER_FEED_EMAILS = `${SITE_ADMIN}meter-feeds/emails/`
export const SITE_ADMIN_METER_FEED_MAILBOX = `${SITE_ADMIN}meter-feeds/emails/mailbox/`
export const SITE_ADMIN_METER_AUTHORISATIONS = `${SITE_ADMIN}meter-authorisations/`
export const SITE_ADMIN_METER_AUTHORISATIONS_SUMMARY = `${SITE_ADMIN}meter-authorisations/summary/`
export const SITE_ADMIN_METER_AUTHORISATION_EVIDENCE = (id) =>
    `${SITE_ADMIN}meter-authorisations/${id}/evidence/`
export const SITE_ADMIN_METER_AUTHORISATION_ACTION = (id, action) =>
    `${SITE_ADMIN}meter-authorisations/${id}/${action}/`
// Past data requests to suppliers, and the supplier playbook (how each supplier answers).
export const SITE_ADMIN_METER_HISTORY_REQUESTS = `${SITE_ADMIN}meter-history/requests/`
export const SITE_ADMIN_METER_HISTORY_REQUEST = (id) =>
    `${SITE_ADMIN}meter-history/requests/${id}/`
// send / chase / close / reopen / attach-letter / upload (POST); letter-file /
// letter-template / draft-email (GET).
export const SITE_ADMIN_METER_HISTORY_REQUEST_ACTION = (id, action) =>
    `${SITE_ADMIN}meter-history/requests/${id}/${action}/`
export const SITE_ADMIN_METER_HISTORY_ORG_METERS = `${SITE_ADMIN}meter-history/requests/org-meters/`
export const SITE_ADMIN_METER_HISTORY_SUMMARY = `${SITE_ADMIN}meter-history/requests/summary/`
export const SITE_ADMIN_METER_HISTORY_SUPPLIERS = `${SITE_ADMIN}meter-history/suppliers/`
export const SITE_ADMIN_METER_HISTORY_SUPPLIER = (id) =>
    `${SITE_ADMIN}meter-history/suppliers/${id}/`
export const STAFF_ADMIN_USERS = `${API_ROOT}/api/staff-admin/users/`
export const STAFF_ADMIN_USER_ASSIGN_ROLES = (userId) =>
    `${API_ROOT}/api/staff-admin/users/${userId}/assign-roles/`
// Reset someone's two-factor authentication: POST {reason}. Who may reset whom is
// decided server-side (userprofile/mfa_reset.py); each row's mfa.canReset mirrors it.
export const STAFF_ADMIN_USER_RESET_MFA = (userId) =>
    `${API_ROOT}/api/staff-admin/users/${userId}/reset-mfa/`
export const STAFF_ADMIN_SUMMARY = `${API_ROOT}/api/staff-admin/summary/`
// A location member's grants (location-scoped-member-role): GET lists, PUT replaces
// the whole set with {locations: [{location, access: 'read'}]}.
export const STAFF_ADMIN_USER_LOCATION_ACCESS = (userId) =>
    `${API_ROOT}/api/staff-admin/users/${userId}/location-access/`

// Org-admin user provisioning (org-admin-user-invitation change).
export const STAFF_ADMIN_INVITES = `${API_ROOT}/api/staff-admin/invites/`
export const STAFF_ADMIN_INVITE_DETAIL = (inviteId) =>
    `${API_ROOT}/api/staff-admin/invites/${inviteId}/`
export const STAFF_ADMIN_INVITE_RESEND = (inviteId) =>
    `${API_ROOT}/api/staff-admin/invites/${inviteId}/resend/`
export const STAFF_ADMIN_CREATE_USER = `${API_ROOT}/api/staff-admin/create-user/`
// Public — no session required; the invitee has none yet.
export const INVITE_PEEK = (token) => `${API_ROOT}/api/invites/${token}/`
export const INVITE_ACCEPT = (token) =>
    `${API_ROOT}/api/invites/${token}/accept/`
// The hallway screen's TV link: staff manage it; the screen itself is public, by token.
export const HALLWAY_LINK = `${API_ROOT}/api/hallway/link/`
export const HALLWAY_LINK_DETAIL = (id) => `${API_ROOT}/api/hallway/link/${id}/`
export const HALLWAY_SCREEN = (token) =>
    `${API_ROOT}/api/hallway/screen/${encodeURIComponent(token)}/`

export const UPDATE_SETTINGS = `${API_ROOT}/api/account/settings/`
export const USERS = `${API_ROOT}/api/users/`
export const ISAMS_IMPORT_FAILURES = `${API_ROOT}/api/users/isams-import-failures/`
export const ISAMS_IMPORT_RUNS = `${API_ROOT}/api/users/isams-import-runs/`
export const USER_DETAIL = (userId) => `${API_ROOT}/api/users/${userId}/`
export const USER_BLOCK = (userId) => `${API_ROOT}/api/users/${userId}/block/`
export const USER_BLOCKED_LIST = `${API_ROOT}/api/users/me/blocked/`
export const USER_FOLLOW = (userId) => `${API_ROOT}/api/users/${userId}/follow/`
export const USER_FOLLOWERS = (userId) =>
    `${API_ROOT}/api/users/${userId}/followers/`
export const USER_FOLLOWING = (userId) =>
    `${API_ROOT}/api/users/${userId}/following/`
export const USER_PHOTOS = (userId) => `${API_ROOT}/api/users/${userId}/photos/`

// Finance (tariff cost engine). Reads open to org members; writes admin-gated.
export const FINANCE_TARIFFS = `${API_ROOT}/api/finance/tariffs/`
export const FINANCE_TARIFF_DETAIL = (id) =>
    `${API_ROOT}/api/finance/tariffs/${id}/`
export const FINANCE_BILLS = `${API_ROOT}/api/finance/bills/`
export const FINANCE_BILL_DETAIL = (id) =>
    `${API_ROOT}/api/finance/bills/${id}/`
export const FINANCE_CONTRACTS = `${API_ROOT}/api/finance/contracts/`
export const FINANCE_CONTRACT_DETAIL = (id) =>
    `${API_ROOT}/api/finance/contracts/${id}/`
// Effective per-fuel renewal lead days from the org's enabled
// contract_renewal_due rules (contract-renewal-toolkit D6).
export const FINANCE_CONTRACTS_META = `${API_ROOT}/api/finance/contracts/meta/`

// Bill statements (bill-statement-validation D6): line-level statement CRUD,
// explicit revalidate, and fixed-column CSV line import/template (D5).
export const FINANCE_STATEMENTS = `${API_ROOT}/api/finance/statements/`
export const FINANCE_STATEMENT_DETAIL = (id) =>
    `${API_ROOT}/api/finance/statements/${id}/`
export const FINANCE_STATEMENT_REVALIDATE = (id) =>
    `${API_ROOT}/api/finance/statements/${id}/revalidate/`
export const FINANCE_STATEMENT_LINES_IMPORT = (id) =>
    `${API_ROOT}/api/finance/statements/${id}/lines/import/`
export const FINANCE_STATEMENT_LINES_IMPORT_TEMPLATE = `${API_ROOT}/api/finance/statements/lines/import/template/`

// Org-admin billing (Stripe-backed). Gated behind the `billing_self_service` org flag.
export const BILLING = `${API_ROOT}/api/billing/`
export const BILLING_SUMMARY = `${BILLING}summary/`
export const BILLING_CHECKOUT = `${BILLING}checkout/`
export const BILLING_PORTAL = `${BILLING}portal/`
// Public plan catalog for the marketing pricing page — no auth, no org context.
// Prices live server-side (billing/plans.py) so this page never re-declares them.
export const BILLING_PUBLIC_PLANS = `${BILLING}plans/`

// Public host -> brand resolution (white-label). Must go through API_ROOT like everything else:
// a bare '/api/branding/' resolves against the Vite dev server in development, which answers
// with the SPA's index.html instead of proxying to Django — so branding silently never resolved
// in dev. Same-origin in production made that invisible.
export const BRANDING = `${API_ROOT}/api/branding/`

export const ANALYTICS_OUT_OF_HOURS = `${API_ROOT}/api/analytics/out-of-hours/`

export const OCCUPANCY_SCHEDULE_URL = (orgId) =>
    `${API_ROOT}/api/occupancy/${orgId}/schedule/`
export const OCCUPANCY_AWAY_PERIODS_URL = (orgId) =>
    `${API_ROOT}/api/occupancy/${orgId}/away-periods/`
export const OCCUPANCY_AWAY_PERIOD_DETAIL_URL = (orgId, id) =>
    `${API_ROOT}/api/occupancy/${orgId}/away-periods/${id}/`
export const OCCUPANCY_SCHEDULE_VARIANTS_URL = (orgId) =>
    `${API_ROOT}/api/occupancy/${orgId}/schedule/variants/`
export const OCCUPANCY_SCHEDULE_VARIANT_DETAIL_URL = (orgId, id) =>
    `${API_ROOT}/api/occupancy/${orgId}/schedule/variants/${id}/`
export const OCCUPANCY_BANK_HOLIDAYS_URL = (orgId) =>
    `${API_ROOT}/api/occupancy/${orgId}/bank-holidays/`

// Tenancies (tenancy-model): estate only — every other flavour gets 404. Reads for org
// admins (and a tenant user's own row); writes admin-gated. ?location= / ?status= filters.
export const TENANCIES_URL = `${API_ROOT}/api/tenancies/`
export const TENANCY_DETAIL_URL = (id) => `${API_ROOT}/api/tenancies/${id}/`
// Change of tenancy: boundary reads, the changeover preview (meters, derived reads, cost
// split), ending a tenancy, private read photos, and meter exchanges (admins only).
export const TENANCY_CHANGEOVER_PREVIEW_URL = (id) =>
    `${API_ROOT}/api/tenancies/${id}/changeover-preview/`
export const TENANCY_END_URL = (id) => `${API_ROOT}/api/tenancies/${id}/end/`
export const TENANCY_READS_URL = (id) =>
    `${API_ROOT}/api/tenancies/${id}/reads/`
export const TENANCY_READ_DETAIL_URL = (readId) =>
    `${API_ROOT}/api/tenancies/reads/${readId}/`
export const TENANCY_READ_PHOTO_URL = (readId) =>
    `${API_ROOT}/api/tenancies/reads/${readId}/photo/`
export const METER_EXCHANGES_URL = `${API_ROOT}/api/meter-exchanges/`
export const METER_EXCHANGE_DETAIL_URL = (id) =>
    `${API_ROOT}/api/meter-exchanges/${id}/`

// Tenant recharge statements (tenant-recharge-statements): estate only (404 otherwise) and
// `finance`-gated (403 when off). Everything but `my-statements` needs an org admin.
export const RECHARGE_SETTINGS_URL = `${API_ROOT}/api/recharge/settings/`
export const RECHARGE_METERS_URL = `${API_ROOT}/api/recharge/meters/`
export const RECHARGE_METER_URL = (fuel, id) =>
    `${API_ROOT}/api/recharge/meters/${fuel}/${id}/`
export const RECHARGE_RULES_URL = `${API_ROOT}/api/recharge/rules/`
export const RECHARGE_RULE_URL = (id) => `${API_ROOT}/api/recharge/rules/${id}/`
export const RECHARGE_STATEMENTS_URL = `${API_ROOT}/api/recharge/statements/`
export const RECHARGE_STATEMENT_URL = (id) =>
    `${API_ROOT}/api/recharge/statements/${id}/`
export const RECHARGE_STATEMENT_ACTION_URL = (id, action) =>
    `${API_ROOT}/api/recharge/statements/${id}/${action}/`
export const RECHARGE_ADJUSTMENT_URL = (id, lineId) =>
    `${API_ROOT}/api/recharge/statements/${id}/adjustments/${lineId}/`
export const RECHARGE_STATEMENT_PDF_URL = (id) =>
    `${API_ROOT}/api/recharge/statements/${id}/pdf/`
export const MY_RECHARGE_STATEMENTS_URL = `${API_ROOT}/api/recharge/my-statements/`
export const MY_RECHARGE_STATEMENT_PDF_URL = (id) =>
    `${API_ROOT}/api/recharge/my-statements/${id}/pdf/`

export const BUDGET_LIST = `${API_ROOT}/api/budget/`
export const BUDGET_DETAIL = (id) => `${API_ROOT}/api/budget/${id}/`
export const BUDGET_CAPABILITIES = `${API_ROOT}/api/budget/capabilities/`
export const BUDGET_VS_ACTUAL = `${API_ROOT}/api/budget/vs-actual/`
// Termly budgets generated from the term calendar (openspec term-calendar-import).
export const BUDGET_TERMLY_PLAN_PREVIEW = `${API_ROOT}/api/budget/termly-plan/preview/`
export const BUDGET_TERMLY_PLAN_COMMIT = `${API_ROOT}/api/budget/termly-plan/commit/`

export const TARGET_LIST = `${API_ROOT}/api/target/`
export const TARGET_DETAIL = (id) => `${API_ROOT}/api/target/${id}/`
export const TARGET_PROGRESS = `${API_ROOT}/api/target/progress/`

export const ANALYTICS_WEATHER_CUSUM = `${API_ROOT}/api/analytics/weather-cusum/`

export const CARBON_REPORT = `${API_ROOT}/api/power/carbon-report/`
export const CARBON_REPORT_PREVIEW = `${API_ROOT}/api/power/carbon-report/preview/`
export const CARBON_FACTORS = `${API_ROOT}/api/power/carbon-factors/`
export const CARBON_FACTORS_UPLOAD = `${API_ROOT}/api/power/carbon-factors/upload/`

export const REPORTS_SUBSCRIPTION = `${API_ROOT}/api/reports/subscription/`
export const REPORTS_LOCATION_SUBSCRIPTIONS = `${API_ROOT}/api/reports/subscription/locations/`
export const REPORTS_LOCATION_SUBSCRIPTION_DETAIL = (id) =>
    `${API_ROOT}/api/reports/subscription/locations/${id}/`
export const REPORTS_ORG_DEFAULT_AUDIENCE = `${API_ROOT}/api/reports/org-default-audience/`
export const REPORTS_ORG_DEFAULT_AUDIENCE_DETAIL = (id) =>
    `${API_ROOT}/api/reports/org-default-audience/${id}/`
export const REPORTS_UNSUBSCRIBE = `${API_ROOT}/api/reports/unsubscribe/`

export const WASTE_OVERVIEW = `${API_ROOT}/api/waste/overview/`
export const WASTE = `${API_ROOT}/api/waste/` // list and create

// Education content library (activities/actions catalogue, org recording).
// Gated by the org's `education` feature flag; see education-content-library.
export const EDUCATION_CATEGORIES = `${API_ROOT}/api/education/categories/`
export const EDUCATION_ITEMS = `${API_ROOT}/api/education/items/`
export const EDUCATION_ITEM_DETAIL = (slug) =>
    `${API_ROOT}/api/education/items/${slug}/`
export const EDUCATION_RECORDS = `${API_ROOT}/api/education/records/`
export const EDUCATION_RECORD_DETAIL = (id) =>
    `${API_ROOT}/api/education/records/${id}/`
export const EDUCATION_RECORD_EVIDENCE = (id) =>
    `${API_ROOT}/api/education/records/${id}/evidence/`
export const EDUCATION_RECORD_EVIDENCE_DETAIL = (id, evidenceId) =>
    `${API_ROOT}/api/education/records/${id}/evidence/${evidenceId}/`

// Site-admin authoring (not flag-gated — content can be prepared ahead of rollout).
export const EDUCATION_ADMIN_ITEMS = `${API_ROOT}/api/education/admin/items/`
export const EDUCATION_ADMIN_ITEM_DETAIL = (id) =>
    `${API_ROOT}/api/education/admin/items/${id}/`
export const EDUCATION_ADMIN_ITEM_RESOURCES = (id) =>
    `${API_ROOT}/api/education/admin/items/${id}/resources/`
export const EDUCATION_ADMIN_ITEM_RESOURCE_DETAIL = (id, resourceId) =>
    `${API_ROOT}/api/education/admin/items/${id}/resources/${resourceId}/`
export const EDUCATION_ADMIN_CATEGORIES = `${API_ROOT}/api/education/admin/categories/`
export const EDUCATION_ADMIN_CATEGORY_DETAIL = (id) =>
    `${API_ROOT}/api/education/admin/categories/${id}/`
export const EDUCATION_ADMIN_SUBJECTS = `${API_ROOT}/api/education/admin/subjects/`
export const EDUCATION_ADMIN_SUBJECT_DETAIL = (id) =>
    `${API_ROOT}/api/education/admin/subjects/${id}/`
export const EDUCATION_ADMIN_TOPICS = `${API_ROOT}/api/education/admin/topics/`
export const EDUCATION_ADMIN_TOPIC_DETAIL = (id) =>
    `${API_ROOT}/api/education/admin/topics/${id}/`

export const XMPP_CREDENTIALS = `${API_ROOT}/api/xmpp/credentials`
export const XMPP_BASE_CREDENTIALS = `${API_ROOT}/api/xmpp/base-credentials/`
export const XMPP_AVATARS = `${API_ROOT}/api/xmpp/avatars/`
export const XMPP_DM = (userId) => `${API_ROOT}/api/xmpp/dm/${userId}/`
export const XMPP_DM_MARK_SEEN = (userId) =>
    `${API_ROOT}/api/xmpp/dm/${userId}/seen/`
export const XMPP_DM_LIST = `${API_ROOT}/api/xmpp/dm/`

// Houses and other member groups (openspec member-groups).
export const PUPIL_GROUPS = `${API_ROOT}/api/pupil-groups/`
export const PUPIL_GROUP_STANDINGS = `${API_ROOT}/api/pupil-groups/standings/`
export const userHouse = (userId) => `${API_ROOT}/api/users/${userId}/house/`
